<template>
	<div class="bottom-number-editor">
		<AppBottomSheet :forceWidth="bottomSheetWidth" :active="active" @toggling-popup="v => $emit('toggling-popup', v)" >
			<div class="bottom-number-editor-content" v-if="active">
				<ModalHeader
					no-new-line
					:title="title"
					:button-text="getString('confirm')"
					@click-button="confirmNumber"
				/>
				<div class="container-body">
					<div class="container-info">
						<div class="container-summary">
							<BottomNumberEditorSummary :start="startingNumber" :end="resultNumber" :start-price-name="startPriceName" :discount-name="discountName" :total-name="totalName" :increment-mode="onlyIncrement" />
						</div>
						<div class="container-shortcuts">
							<BottomNumberEditorShortcuts v-if="!customShortcuts" :only-decrement="onlyDecrement" @shortcut="applyShortcut" :no-hundred-percent="noHundredPercent" />
						</div>
						<div class="container-shortcuts" v-if="customShortcuts">
							<div class="shortcut" v-for="(item, index) in shortcuts" :key="shortcuts.business_shortcut_id" :class="shortcutColor(index)" @click="useShortcut(item.shortcut_value)">
								{{currencyStore.readableCurrencyValue(item.shortcut_value)}}
							</div>
						</div>
						<div class="container-shortcuts coupon-scrollable" v-if="!customShortcuts">
							<div class="shortcut coupon low" :class="{active: coupon.selected}"
								 v-for="coupon in couponsFiltered"
								 :ref="'coupon-' + coupon.business_coupon_id"
								 @click="applyCoupon(coupon)"
							>
								<div class="vertical-slider" v-longpress="couponLongPressConfig(coupon)"
									 :class="{'active': couponValueToShow === coupon.business_coupon_id}"
									 @click.stop="() => singleClickBehavior(coupon)"
								>
									<p>{{coupon.displayName}}</p>
									<p>{{coupon.displayValue}}</p>
								</div>
							</div>
						</div>
					</div>
					<div class="container-numpad">
						<BottomNumberEditorNumpad ref="numpad" @change-number="changeNumber" @perform-action="performAction" :no-actions="noActions" :nothing-to-delete="nothingChanged" :startingNumber="startingNumber" :increment-mode="onlyIncrement" />
					</div>
				</div>
			</div>
		</AppBottomSheet>
	</div>
</template>

<script>
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import {useCurrencyStore, useLanguageStore} from "@/shared/stores";
import BottomNumberEditorSummary from "@/console-new/components/ops/counter/BottomNumberEditorSummary.vue";
import BottomNumberEditorNumpad from "@/console-new/components/ops/counter/BottomNumberEditorNumpad.vue";
import BottomNumberEditorShortcuts from "@/console-new/components/ops/counter/BottomNumberEditorShortcuts.vue";
import {useCounterStore, useCouponsStore} from "@/console-new/stores";
import {useUserStore} from "@/user/stores";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const counterStore = useCounterStore();
		const userStore = useUserStore();
		const currencyStore = useCurrencyStore();
		const couponsStore = useCouponsStore();
		return {getString: languageStore.getString, counterStore, currencyStore, userStore, languageStore, couponsStore};
	},
	name: "BottomNumberEditor",
	components: {BottomNumberEditorShortcuts, BottomNumberEditorNumpad, BottomNumberEditorSummary, ModalHeader, AppBottomSheet},
	mixins: [WindowWidth],
	emits: ["toggling-popup", "confirm-number"],
	props: {
		active: {
			type: Boolean,
			default: true,
		},
		noActions: {
			type: Boolean,
			default: false,
		},
		showAllShortcuts: {
			type: Boolean,
			default: false,
		},
		title: {
			type: String,
			required: true,
		},
		startingNumber: {
			type: Number,
			required: true,
		},
		startPriceName: {
			type: String,
			default: null,
		},
		discountName: {
			type: String,
			default: null,
		},
		totalName: {
			type: String,
			default: null,
		},
		onlyDecrement: {
			type: Boolean,
			default: false,
		},
		onlyIncrement: {
			type: Boolean,
			default: false,
		},
		customShortcuts: {
			type: Boolean,
			default: false,
		},
		noHundredPercent: {
			type: Boolean,
			default: false,
		},
		discountMode: {
			type: String,
			default: null,
		}
	},
	data() {
		return {
			resultNumber: this.onlyIncrement ? 0 : this.startingNumber,
			appliedCoupons: [],
			couponValueToShow: null,
			longPressInvoked: false,
		}
	},
	computed: {
		bottomSheetWidth() {
			if(this.windowWidth < 800) {
				return "100%";
			} else {
				return "800px";
			}
		},
		nothingChanged() {
			if(this.onlyIncrement) {
				return this.resultNumber === 0;
			}
			return this.resultNumber === this.startingNumber;
		},
		shortcuts() {
			let returningArray = this.counterStore.shortcuts ? this.counterStore.shortcuts : [];
			if(!this.showAllShortcuts) {
				returningArray = returningArray.filter(shortcut => shortcut.shortcut_value <= this.startingNumber);
			}
			returningArray = returningArray.sort((a,b) => a.order_number - b.order_number);
			return returningArray;
		},
		couponsFiltered() {
			let coupons = [];

			if(this.couponsStore.businessCoupons && this.discountMode === "total") {
				Object.values(this.couponsStore.businessCoupons).forEach((coupon) => {

					coupon.displayName = coupon.code.length > 8  ? coupon.code.substring(0, 8) + '..' : coupon.code;
					coupon.displayValue = coupon.fixed_value ?
						this.currencyStore.readableCurrencyValue(coupon.fixed_value) :
						coupon.percentage_value + '%';
					coupon.selected = this.appliedCoupons.length > 0 &&
						this.appliedCoupons[0].business_coupon_id === coupon.business_coupon_id;

					coupons.push(coupon);

				});
			}

			return coupons;
		}
	},
	methods: {
		confirmNumber() {
			this.$emit("confirm-number", this.resultNumber, this.appliedCoupons.length > 0 ? this.appliedCoupons : null);
		},
		changeNumber(number) {
			this.appliedCoupons = [];
			this.resultNumber = number;

			if(this.onlyIncrement && this.resultNumber < 0) {
				// In increment mode, ensure the result is not negative
				this.resultNumber = 0;
			} else if(this.onlyDecrement && this.resultNumber > this.startingNumber) {
				// In decrement mode, ensure the result doesn't exceed starting number
				this.resultNumber = this.startingNumber;
			}
		},
		performAction(action, number) {
			let actionNumber = 0;
			if(action === "percent") {
				actionNumber = Math.round(this.startingNumber * number);
				this.resultNumber = this.startingNumber + actionNumber;
			} else if(action === "abs") {
				actionNumber = number;
				this.resultNumber = this.startingNumber + actionNumber;
			}
			this.resultNumber = this.resultNumber < 0 ? 0 : this.resultNumber;
			if(this.onlyDecrement && this.resultNumber > this.startingNumber) {
				actionNumber = 0;
				this.resultNumber = this.startingNumber;
			}
			if(this.onlyIncrement && this.resultNumber < this.startingNumber) {
				actionNumber = 0;
				this.resultNumber = this.startingNumber;
			}
			return actionNumber;
		},
		useShortcut(value){
			this.changeNumber(value);
			this.confirmNumber();
		},
		applyCoupon(coupon) {
			this.resultNumber = this.startingNumber;

			if(
				this.appliedCoupons.length === 0 ||
				(this.appliedCoupons.length > 0 && this.appliedCoupons[0].business_coupon_id !== coupon.business_coupon_id)
			) {
				let actionNumber = null
				if (coupon.percentage_value) {
					actionNumber = this.performAction('percent', -coupon.percentage_value/100, true);
				}
				else if (coupon.fixed_value) {
 					actionNumber = this.performAction('abs', -coupon.fixed_value, true);
				}
				this.appliedCoupons = [{
					business_coupon_id: coupon.business_coupon_id,
					discount: Math.abs(actionNumber)
				}];
			}
			else {
				this.appliedCoupons = [];
			}
		},
		shortcutColor(index) {
			if(index <= 2){
				return "high";
			} else if(index <= 5) {
				return "medium";
			} else {
				return "low";
			}
		},
		applyShortcut(number) {
			this.appliedCoupons = [];
			this.performAction('percent', number/100);
			if(this.$refs.numpad) {
				this.$refs.numpad.currentNumber = 0;
			}
		},
		couponLongPressConfig(coupon) {
			return {
				handler: (event) => {
					this.longPressInvoked = true;
					this.couponValueToShow = coupon.business_coupon_id;
				},
				ignoreMouse: false,
				stopHandler: (e) => {
					e.stopPropagation();
					this.couponValueToShow = null;
				}
			};
		},
		singleClickBehavior(coupon) {
			if (!this.longPressInvoked) {
				this.$refs['coupon-' + coupon.business_coupon_id][0].click();
			}
			this.longPressInvoked = false;
		},
	},
	mounted() {
		if(this.onlyDecrement || (this.customShortcuts && this.onlyIncrement)) {
			this.counterStore.loadShortcuts();
		}
	},
	watch: {
		startingNumber() {
			this.resultNumber = this.startingNumber;
		},
		active(newVal, oldVal) {
			if(newVal && !oldVal) {
				this.resultNumber = this.onlyIncrement ? 0 : this.startingNumber;
				this.$nextTick(() => {
					if(this.$refs.numpad) {
						this.$refs.numpad.currentNumber = 0;
					}
					if(!this.customShortcuts) {
						this.couponsStore.getBusinessCoupons();
					}
				});
			}
			else {
				this.appliedCoupons = [];
			}
		},
		'userStore.currBusinessId': function(newVal, oldVal) {
			if(newVal !== null && newVal !== oldVal) {
				if(this.onlyDecrement) {
					this.counterStore.loadShortcuts();
				}
			}
		}
	}
}
</script>

<style scoped lang="scss">
.container-body {
	display: flex;
	padding-top: 10px;

	.container-info {
		width: 50%;
		display: flex;
		flex-direction: column;

		.container-summary {
			width: 100%;
			display: flex;
			align-items: center;
			justify-content: center;

		}

		.container-shortcuts {
			width: 100%;
			display: flex;
			margin-top: 10px;
			flex-wrap: wrap;
			gap: 9px;
			max-width: calc(((100% / 2) - 6px) * 3);

			&.coupon-scrollable {
				max-height: 180px;
				overflow: scroll;
				@include hideScrollbar();
			}

			@media (max-width: 900px) {
				flex-wrap: nowrap;
				overflow-x: auto;
				padding-bottom: 10px;
				gap: 6px;
				padding-right: 6px;
			}

			.shortcut {
				width: calc((100% / 3) - 9px);
				display: flex;
				align-items: center;
				justify-content: flex-start;
				flex-direction: column;
				gap: 10px;
				padding: 15px 12px;
				cursor: pointer;
				box-shadow: 0 2px 3px var(--color-box-shadow-3);
				transition: box-shadow 0.4s $transition;
				border-radius: 13px;
				font-weight: 500;
				overflow: hidden;
				height: 53px;
				position: relative;
				&.coupon {
					padding: 0;
				}
				.vertical-slider {
					transition: top 0.4s $transition;
					position: absolute;
					top: 0;
					left: 0;
					height: 106px;
					width: 100%;
					p {
						line-height: 53px;
						text-align: center;
					}
					&.active {
						top: -53px;
					}
				}
				&.active {
					border: 1px solid var(--color-primary);
				}

				&:hover {
					box-shadow: 0 2px 3px var(--color-bg-alpha-0);
				}

				&.high {
					background-color: rgba(var(--rgb-primary), .45);
				}

				&.low {
					background-color: rgba(var(--rgb-primary), .1);
				}

				&.medium {
					background-color: rgba(var(--rgb-primary), .25);
				}

				@media (max-width: 600px) {
					font-size: 14px;
				}
				@media (max-width: 900px) {
					width: 100%;
					min-width: 100px;
				}

			}
		}
	}

	.container-numpad {
		width: 50%;
		display: flex;
		align-items: center;
		justify-content: flex-end;
		padding: 0 0 0 20px;

		:deep(.numpad) {

			@media (max-width: 400px) {
				gap: 5px;

				.row {
					gap: 5px;

					.btn {
						width: 60px;
						height: 60px;
						border-radius: 18px;

						.btn-inner {
							border-radius: 15px;
							width: 77%;
							height: 77%;
							font-size: 36px;
						}

						&.action .btn-inner {
							font-size: 32px;
						}
					}
				}
			}
		}
	}
	
	@media (max-width: 900px) {
		flex-direction: column;
		padding-top: 0;

		.container-info {
			width: 100%;
			margin-bottom: 15px;
		}

		.container-numpad {
			width: 100%;
			padding: 0;
			margin-bottom: 5px;
		}

	}
}
</style>